
import { Component, Prop, Watch } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import ResourceCards from "@/components/card/ResourceCards.vue";
import CREATE_MANAGER_CONTRACTOR from "@/apollo/managers/CreateManagerContractor.gql";
import INSET_USER_FAVORITE_ONE from "@/apollo/user/InsertUserFavoriteOne.gql";
import DELETE_USER_FAVORITE_BY_PK from "@/apollo/user/DeleteUserFavoriteByPk.gql";
import ResourceAvatar from "@/components/card/ResourceAvatar.vue";
import GetRelativeRouteNameMixin from "@/mixins/route/GetRelativeRouteNameMixin.vue";
import TableListChipNew from "@/components/table/table-list-chip-new.vue";
import CardBodyItem from "@/components/card/CardBodyItemNew.vue";
import HeartIcon from "@/components/icons/HeartIcon.vue";
import ChatIcon from "@/components/icons/ChatIconNew.vue";
import ChatIconHover from "@/components/icons/ChatIconHoverNew.vue";
import Card from "@/components/card/Card.vue";
import ActionSection from "@/components/card/ActionSection.vue";
import EditIcon from "@/components/icons/edit.vue";
import getCountryCode from "@/utils/getCountryCode";
import CardAvatar from "@/components/card/CardAvatar.vue";
import Chip from "@/components/filters/Chip.vue";
import TableAvatar from "@/components/table/TableAvatar.vue";
import TableIcon from "@/components/table/table-icon.vue";
import Processing from "@/components/common/Processing.vue";
import CardInfoChip from "@/components/card/CardInfoChip.vue";
import UiButton from "@/components/button/ui-button.vue";
import CountryFlag from 'vue-country-flag';
import ItemChipsWithMore from "@/components/common/ItemChipsWithMore.vue";
import AddContractorToProject from "@/components/contractors/AddContractorToProject.vue";
import ContractorResendInvitation from "@/components/invite/ContractorResendInvitationNew.vue";
import LikeIcon from "@/components/icons/like-icon.vue";
import UnlikeIcon from "@/components/icons/unlikeIcon.vue";
import { Contractor, Tag, User } from "@/generated/graphql";
import { NOTIFICATION } from "@/entities/Events";
import Roles from "@/entities/Roles";
import RouteNames from "@/entities/RouteNames";
import RouteParams from "@/entities/RouteParams";
import Contractor_PROJECTS from "@/apollo/contractors/GetContractorProjects.gql";
import ManagerStatus from "@/entities/manager/ManagerStatus";
import Message from "@/data/notificationsMessages";
import ResendInvitationPlaceholder from "@/components/invite/ResendInvitationPlaceholder.vue";
import Badge from "@/components/common/BadgeNew.vue";
import { emailStatusText } from "@/data/userStatuses";
import BadgeColor from "@/entities/BadgeColor";
import EmployeeTag from "@/components/employee/EmployeeTagNew.vue";

interface ManagerContractorInsertObject {
  contractor_id: string;
  manager_id: string;
}

interface FavouriteContractorInsertObject {
  favorited_by_id: string;
  favorited_id: string;
}

interface IActionDefault {
  leftTitle: string;
  leftValue: number | string;
  rightTitle: string;
  rightValue: number | string;
}

interface IActionError {
  title: string;
  description: number | string;
  actionButton: string;
}

interface IActionSection {
  state: boolean;
  default: IActionDefault;
  error: IActionError;
}

@Component({
  components: {
    EmployeeTag,
    ResendInvitationPlaceholder,
    ContractorResendInvitation,
    TableListChipNew,
    CountryFlag,
    CardBodyItem,
    Chip,
    Card,
    EditIcon,
    ChatIconHover,
    LikeIcon,
    UnlikeIcon,
    ResourceAvatar,
    ActionSection,
    ResourceCards,
    TableAvatar,
    TableIcon,
    UiButton,
    Processing,
    CardInfoChip,
    ItemChipsWithMore,
    CardAvatar,
    HeartIcon,
    ChatIcon,
    AddContractorToProject,
    Badge,
  },
  apollo: {
    projects: {
      query: Contractor_PROJECTS,
      variables() {
        return this.conditions;
      },
      update(data) {
        const totalActiveProj = data.project.filter(
          (proj) => !proj.completed
        ).length;
        this.totalActiveProj = totalActiveProj;
        return data;
      },
    },
  },
})
export default class ContractorCard extends mixins(GetRelativeRouteNameMixin) {
  @Prop({ type: String, default: undefined })
  readonly currentUserManagerId?: string;
  @Prop({ type: String, default: "MANAGER" }) readonly currentUserRole!: string;
  @Prop({ type: String, required: true }) readonly currentUserId!: string;
  @Prop({ type: String, required: true }) readonly givenName!: string;
  @Prop({ type: String, required: true }) readonly familyName!: string;
  @Prop({ type: String, required: true }) readonly contractorUserId!: string;
  @Prop({ type: String, default: undefined }) readonly contractorId?: string;
  @Prop({ type: String, default: null }) readonly avatar!: string | null;
  @Prop({ type: String, default: null }) readonly currency!: string | null;
  @Prop({ type: String, default: null }) readonly location!: string | null;
  @Prop({ type: String, default: null }) readonly jobTitle!: string | null;
  @Prop({ type: String, default: null }) readonly companyName!: string | null;
  @Prop({ type: Array, default: () => [] }) readonly skills!: any[];
  @Prop({ type: Number, default: null }) readonly maxRate!: number | null;
  @Prop({ type: Number, default: null }) readonly minRate!: number | null;
  @Prop({ type: Array, default: () => [] }) readonly managers!: any[];
  @Prop({ type: Array, default: () => [] }) readonly contractors!: Contractor[];
  @Prop({ type: Array, default: () => [] }) readonly companyNotifications!: {
    [user_id: string]: string;
  }[];
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: String, default: undefined }) readonly status?: string;
  @Prop({ type: String, default: undefined }) readonly companyId?: string;
  @Prop({ type: Boolean, default: false }) readonly canNavigate!: boolean;
  @Prop({ type: Array, default: () => [] }) readonly favorited!: any[];
  @Prop({ type: Boolean, default: false }) readonly userAvailable!: boolean;
  @Prop({ type: Boolean, default: false }) readonly showAccessGranted!: boolean;
  @Prop({ type: Boolean, default: false }) readonly isMarketplace!: boolean;
  @Prop({ type: Boolean, default: false }) readonly canAddProject!: boolean;
  @Prop({ type: Object, default: () => ({}) }) readonly queryVars!: object;
  @Prop({ type: Object, default: () => ({}) }) readonly tag?: object;
  @Prop({ type: Boolean, default: false })
  readonly favoriteContractors!: object;
  @Prop({ type: Boolean, default: false }) readonly emailConfirmed!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly absoluteDetailsLinkPath!: boolean;
  @Prop({ type: Boolean, default: false })
  readonly managerAccessApproved!: boolean;
  @Prop({ type: String, default: undefined }) readonly userUpdatedDate?: string;

  BadgeColor: typeof BadgeColor = BadgeColor;
  routes = RouteNames;
  processing: boolean = false;
  totalActiveProj: any = null;
  emailStatusText: string = emailStatusText;
  isHoveredChat: boolean = false;
  isHoveredLike: boolean = false;
  favoriteContractor: boolean = false;

  get isEmailUnconfirmed(): boolean {
    const contractorExist = [this.companyId, this.contractorId].every((id) =>
      Boolean(id)
    );
    return contractorExist && !this.emailConfirmed;
  }

  get conditions(): Object {
    return {
      where: {
        project_activities: {
          contractor: {
            user_id: {
              _eq: this.contractor.user.id,
            },
          },
        },
      },
    };
  }

  get contractor(): Contractor | undefined {
    return this.contractors[0];
  }

get country() {
        const userCountry = this.contractor.user.profile.country ? this.contractor.user.profile.country : this.contractor.user.user_profile_by_managements[0]?.country;
        return getCountryCode(userCountry);
    }

  get isEmployeeTagShown(): boolean {
    if (!this.contractor) {
      return false;
    }

    const managerCondition =
      this.currentUserRole !== Roles.MANAGER || this.managerAccessApproved;
    const { deleted, approved } = this.contractor;

    return !deleted && approved && managerCondition;
  }

  get appliedTag(): Tag | undefined {
    return this.contractors[0]?.tag || undefined;
  }

  get getActionSectionValues(): IActionSection {
    // use this function to get the values for the action section
    const actionSectionResult: IActionSection = {
      state: this.isEmailUnconfirmed,
      default: {
        leftTitle: "Active Client",
        leftValue: this.totalActiveProj,
        rightTitle: "",
        rightValue: "",
      },
      error: {
        title: this.emailStatusText,
        description: "",
        actionButton: 'resend',
      },
    };
    return actionSectionResult;
  }

  get displaySkills(): boolean {
    return !!this.contractorSkills?.length;
  }

  get isNotificationsDisabled(): boolean {
    return this.companyNotifications.length > 0;
  }

  get isManagerSuspended(): boolean {
    return this.status === ManagerStatus.SUSPENDED;
  }
  get contractorTag(): any {
    return this.tag || "n/a";
  }
  get contractorAvatar(): string {
    return this.avatar || "";
  }

  get allowAddProject(): boolean {
    return this.hasAccess && this.canAddProject;
  }

  get isCompanyContractors(): boolean {
    return this.$route.name === RouteNames.COMPANY_DATABASE;
  }

  get contractorDetailsLink(): RouteParams<{ id: string }> {
    if (!this.isMarketplace && this.hasAccess) {
      return {
        name: this.absoluteDetailsLinkPath
          ? RouteNames.CONTRACTOR_DETAILS_ABOUT
          : this.getRelativeRouteName(RouteNames.CONTRACTOR_DETAILS_ABOUT),
        params: { id: this.contractorId! },
      };
    }
    return {
      name: this.absoluteDetailsLinkPath
        ? RouteNames.CONTRACTOR_PUBLIC_PAGE
        : this.getRelativeRouteName(RouteNames.CONTRACTOR_PUBLIC_PAGE),
      params: { id: this.contractorUserId },
    };
  }

  get belongsToCompany(): boolean | undefined {
    if (this.contractors.length === 0) {
      return false;
    }

    const contractorDeleted = this.contractors[0].deleted;
    const contractorApproved = this.contractors[0].approved;
    return !contractorDeleted && contractorApproved;
  }

  get contractorInitials(): string {
    return `${this.givenName![0]}${this.familyName![0]}`;
  }

  get contractorFullName(): string {
    return `${this.givenName} ${this.familyName}`;
  }

  get job(): string {
    return this.jobTitle || "n/a";
  }

  get contractorLocation(): string {
    return this.location || "n/a";
  }

  get contractorSkills(): any[] {
    if (this.skills?.length) {
      return this.skills.reduce((acc: any[], { skill }) => {
        if (skill.name) {
          acc.push({ name: skill.name, id: skill.id });
        }
        return acc;
      }, []);
    }
    return [];
  }

  get contractorMinRate(): string | undefined {
   const minRate = this.contractor.user.profile.min_rate || this.contractor.user.user_profile_by_managements[0]?.min_rate;

    if (!minRate) {
      return "n/a";
    }

    return `$${minRate}`;
  }

  get contractorMaxRate(): string | undefined {
    const maxRate = this.contractor.user.profile.max_rate || this.contractor.user.user_profile_by_managements[0]?.max_rate;
		const minRate = this.contractor.user.profile.min_rate || this.contractor.user.user_profile_by_managements[0]?.min_rate;
		const rateUnit = this.contractor.user.profile.rate_unit || this.contractor.user.user_profile_by_managements[0]?.rate_unit;

    if (!maxRate) {
      return ``;
    }
    if (!maxRate && minRate) {
      return `${rateUnit}`;
    }
    return `-$${maxRate}`;
  }

  get contractorRateUnit(): string | undefined {
   const maxRate = this.contractor.user.profile.max_rate || this.contractor.user.user_profile_by_managements[0]?.max_rate;
		const minRate = this.contractor.user.profile.min_rate || this.contractor.user.user_profile_by_managements[0]?.min_rate;
		const rateUnit = this.contractor.user.profile.rate_unit || this.contractor.user.user_profile_by_managements[0]?.rate_unit;

    if (!minRate && !maxRate && !rateUnit) {
      return "";
    }

    return `/${rateUnit}`;
  }

  get displayManagers(): boolean {
    return !!this.managers.length;
  }

  get isAdmin(): boolean {
    return this.currentUserRole == Roles.ADMIN;
  }

  get managersInfo(): {} | string {
    if (!this.managers.length) {
      return [];
    }
    return this.managers.map(({ manager, manager_id }) => {
      return {
        name: `${manager.user.given_name} ${manager.user.family_name}`,
        id: manager_id,
        avatar: manager.user.avatar,
      };
    });
  }

  get navigateToManagersTab(): RouteParams<{ id: string }> {
    if (!this.isMarketplace && this.hasAccess) {
      return {
        name: RouteNames.CONTRACTOR_DETAILS_MANAGERS,
        params: { id: this.contractorId! },
      };
    }

    return {
      name: RouteNames.CONTRACTOR_PUBLIC_PAGE,
      params: { id: this.contractorUserId },
    };
  }

  get renderManagers(): boolean {
    return this.isAdmin && !this.isMarketplace;
  }

  get managerHasAccess(): boolean {
    if (!this.managers.length) {
      return false;
    }

    return this.managers.some((m: any) => {
      return m.manager_id === this.currentUserManagerId && m.approved;
    });
  }

  get hasAccess(): boolean {
    return this.managerHasAccess || this.isAdmin;
  }

  get showHasAccessBadge(): boolean {
    return this.managerHasAccess && this.isCompanyContractors;
  }

  get ableToLike(): boolean {
    if (this.isMarketplace) {
      return false;
    }
    return this.hasAccess && !this.favoriteContractor;
  }

  get favoriteEntryId(): string | undefined {
    if (!this.favorited.length) {
      return undefined;
    }
    return this.favorited[0].id;
  }

  get cardStyle(): Record<string, boolean> {
    return {
      ["bg-gray-200 text-gray-500"]: !this.emailConfirmed,
    };
  }

  @Watch("favorited.length", { immediate: true })
  onFavoritedChange() {
    this.favoriteContractor = Boolean(this.favorited.length);
  }

  async handleRequestCompanyContractor(): Promise<any> {
    if (this.contractorId && this.currentUserManagerId) {
      const insertObj = {
        contractor_id: this.contractorId,
        manager_id: this.currentUserManagerId,
      };
      this.processing = true;
      await this.createManagerContractor(insertObj);
      this.processing = false;
      this.$emit("refetch");
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(
          `You have requested access to ${this.contractorFullName}`
        )
      );
    }
  }

  createManagerContractor(object: ManagerContractorInsertObject) {
    return this.$apollo.mutate({
      mutation: CREATE_MANAGER_CONTRACTOR,
      variables: {
        object,
      },
    });
  }

  async handleLikeContractor(): Promise<any> {
    const insertObj = {
      favorited_by_id: this.currentUserId,
      favorited_id: this.contractorUserId!,
    };
    this.processing = true;
    await this.addContractorToFavorites(insertObj);
    this.favoriteContractor = true;
    this.processing = false;
  }

  addContractorToFavorites(insertObj: FavouriteContractorInsertObject) {
    return this.$apollo.mutate({
      mutation: INSET_USER_FAVORITE_ONE,
      variables: {
        object: insertObj,
        id: this.currentUserId,
      },
    });
  }

  async handleUnlikeContractor(): Promise<any> {
    if (this.favoriteEntryId) {
      this.processing = true;
      await this.deleteFavoriteEntry(this.favoriteEntryId);
      this.favoriteContractor = false;
      this.processing = false;
      if (this.favoriteContractors) {
        this.$emit("refetch");
      }
    }
  }

  deleteFavoriteEntry(id: string) {
    return this.$apollo.mutate({
      mutation: DELETE_USER_FAVORITE_BY_PK,
      variables: {
        favoriteEntryId: id,
      },
    });
  }
}
